import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

// Error Boundary - prevents full page crash
class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    componentDidCatch(error, info) { console.error('ErrorBoundary:', error, info); }
    render() {
        if (this.state.hasError) {
            return (
                <div className="flex items-center justify-center min-h-[50vh] p-8">
                    <div className="text-center max-w-md">
                        <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                        </div>
                        <h3 className="text-lg font-bold mb-2">Bir hata olustu</h3>
                        <p className="text-sm text-muted-foreground mb-4">{this.state.error?.message || 'Beklenmeyen bir hata'}</p>
                        <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
                            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
                            Sayfayi Yenile
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

// Pages - Core (eager load)
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ModulePage from "@/pages/ModulePage";
import LessonPage from "@/pages/LessonPage";

// Pages - Lazy loaded
const ProfilePage = React.lazy(() => import("@/pages/ProfilePage"));
const MevzuatPortalPage = React.lazy(() => import("@/pages/MevzuatPortalPage"));
const AllTrainingsPage = React.lazy(() => import("@/pages/AllTrainingsPage"));
const KurumsalPage = React.lazy(() => import("@/pages/KurumsalPage"));
const BireyselPage = React.lazy(() => import("@/pages/BireyselPage"));
const TopicPage = React.lazy(() => import("@/pages/TopicPage"));
const QuizPage = React.lazy(() => import("@/pages/QuizPage"));
const QuizAnswerSheetPage = React.lazy(() => import("@/pages/QuizAnswerSheetPage"));
const EmbedTrainingPage = React.lazy(() => import("@/pages/EmbedTrainingPage"));
const CertificatePage = React.lazy(() => import("@/pages/CertificatePage"));
const VerifyCertificatePage = React.lazy(() => import("@/pages/VerifyCertificatePage"));
const WeakTopicsPage = React.lazy(() => import("@/pages/WeakTopicsPage"));
const ContentUpdatesPage = React.lazy(() => import("@/pages/ContentUpdatesPage"));
const LeaderboardPage = React.lazy(() => import("@/pages/LeaderboardPage"));
const LandingPage = React.lazy(() => import("@/pages/LandingPage"));
const DemoShowcasePage = React.lazy(() => import("@/pages/DemoShowcasePage"));
const DenetimKanitPage = React.lazy(() => import("@/pages/DenetimKanitPage"));
const DenetimOzetPage = React.lazy(() => import("@/pages/DenetimOzetPage"));
const SozlesmePage = React.lazy(() => import("@/pages/SozlesmePage"));
const RegulatorShareViewPage = React.lazy(() => import("@/pages/RegulatorShareViewPage"));
const KvkkAydinlatmaPage = React.lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.KvkkAydinlatmaPage })));
const CerezPolitikasiPage = React.lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.CerezPolitikasiPage })));
const KullanimSartlariPage = React.lazy(() => import("@/pages/LegalPages").then(m => ({ default: m.KullanimSartlariPage })));
const BulletinManagePage = React.lazy(() => import("@/pages/tenant-admin/BulletinManagePage"));
const InternalDocsPage = React.lazy(() => import("@/pages/tenant-admin/InternalDocsPage"));
const AuditorPage = React.lazy(() => import("@/pages/AuditorPage"));
const RegulatorReportPage = React.lazy(() => import("@/pages/RegulatorReportPage"));
const ExamsPage = React.lazy(() => import("@/pages/ExamsPage"));
const LiveSessionsPage = React.lazy(() => import("@/pages/LiveSessionsPage"));
import LiveRoomPage from "@/pages/LiveRoomPage";
const InstructorDashboardPage = React.lazy(() => import("@/pages/InstructorDashboardPage"));
import KeycloakCallbackPage from "@/pages/KeycloakCallbackPage";
const ManagerDashboardPage = React.lazy(() => import("@/pages/ManagerDashboardPage"));
const ManagerTeamMemberPage = React.lazy(() => import("@/pages/ManagerTeamMemberPage"));
import ComplianceDashboardPage from "@/pages/ComplianceDashboardPage";
import ExecutiveDashboardPage from "@/pages/ExecutiveDashboardPage";
const AdminDashboard = React.lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = React.lazy(() => import("@/pages/admin/AdminUsers"));
const AdminContent = React.lazy(() => import("@/pages/admin/AdminContent"));
const AdminReports = React.lazy(() => import("@/pages/admin/AdminReports"));
const AdminAuditLogs = React.lazy(() => import("@/pages/admin/AdminAuditLogs"));
const AdminSettings = React.lazy(() => import("@/pages/admin/AdminSettings"));
import AppLayout from "@/components/AppLayout";
const AdminBBBPage = React.lazy(() => import("@/pages/admin/AdminBBBPage"));
const TrainingPeriodsPage = React.lazy(() => import("@/pages/admin/TrainingPeriodsPage"));
const AdminNotificationsPage = React.lazy(() => import("@/pages/admin/AdminNotificationsPage"));
const AdminDocumentsPage = React.lazy(() => import("@/pages/admin/AdminDocumentsPage"));
const AdminContentGenPage = React.lazy(() => import("@/pages/admin/AdminContentGenPage"));

// Multi-Tenant Pages
import TenantSignupPage from "@/pages/TenantSignupPage";
const SuperAdminDashboard = React.lazy(() => import("@/pages/super-admin/SuperAdminDashboard"));
const SuperAdminPackagesPage = React.lazy(() => import("@/pages/super-admin/SuperAdminPackagesPage"));
const SuperAdminSectorsPage = React.lazy(() => import("@/pages/super-admin/SuperAdminSectorsPage"));
const SuperAdminTenantsPage = React.lazy(() => import("@/pages/super-admin/SuperAdminTenantsPage"));
const SuperAdminTenantCreatePage = React.lazy(() => import("@/pages/super-admin/SuperAdminTenantCreatePage"));
const SuperAdminTenantDetailPage = React.lazy(() => import("@/pages/super-admin/SuperAdminTenantDetailPage"));
const SuperAdminCMSPage = React.lazy(() => import("@/pages/super-admin/SuperAdminCMSPage"));
const SuperAdminDemoRequestsPage = React.lazy(() => import("@/pages/super-admin/SuperAdminDemoRequestsPage"));
const SuperAdminRegulatoryValuesPage = React.lazy(() => import("@/pages/super-admin/SuperAdminRegulatoryValuesPage"));
const TenantAdminDashboard = React.lazy(() => import("@/pages/tenant-admin/TenantAdminDashboard"));
const TenantCurriculumPage = React.lazy(() => import("@/pages/tenant-admin/TenantCurriculumPage"));
const TenantSettingsPage = React.lazy(() => import("@/pages/tenant-admin/TenantSettingsPage"));
const InstructorManagementPage = React.lazy(() => import("@/pages/tenant-admin/InstructorManagementPage"));
const UserManagementPage = React.lazy(() => import("@/pages/tenant-admin/UserManagementPage"));
const WebhooksPage = React.lazy(() => import("@/pages/tenant-admin/WebhooksPage"));
const DepartmentAssignmentsPage = React.lazy(() => import("@/pages/tenant-admin/DepartmentAssignmentsPage"));
const AgentTrainingPage = React.lazy(() => import("@/pages/tenant-admin/AgentTrainingPage"));
import AcceptInvitePage from "@/pages/AcceptInvitePage";

// Protected Route Component
const ProtectedRoute = ({
    children,
    adminOnly = false,
    requireReports = false,
    requireAudit = false,
    requireAuditor = false,
    requireSuperAdmin = false,
    requireTenantAdmin = false,
    requireInstructor = false,
}) => {
    const { user, loading, canAccessAdmin, canViewReports, canViewAudit, canAccessAuditor } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Super Admin sayfaları için kontrol
    if (requireSuperAdmin && user.role !== 'super_admin') {
        return <Navigate to="/dashboard" replace />;
    }

    // Admin sayfaları için kontrol
    if (adminOnly && !canAccessAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    // Rapor sayfaları için kontrol (doğrudan /dashboard'a yönlendir)
    if (requireReports && !canViewReports) {
        return <Navigate to="/dashboard" replace />;
    }

    // Denetim izi sayfaları için kontrol (doğrudan /dashboard'a yönlendir)
    if (requireAudit && !canViewAudit) {
        return <Navigate to="/dashboard" replace />;
    }

    // Denetçi paneli için kontrol
    if (requireAuditor && !canAccessAuditor) {
        return <Navigate to="/dashboard" replace />;
    }

    // Tenant Admin sayfaları için kontrol
    if (requireTenantAdmin && !['super_admin', 'tenant_admin'].includes(user?.role)) {
        return <Navigate to="/dashboard" replace />;
    }

    // Öğretmen sayfaları için kontrol
    if (requireInstructor && !user?.is_instructor && !canAccessAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (user) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};

// Root / anasayfa — her zaman tanıtım/LandingPage gösterir. Giriş yapmış
// kullanıcılar buradan logo/anasayfa linkine tıkladığında da tanıtımı
// görebilsin (rebrand anlatımına, sözleşmeye, denetçi görünümüne hızlı
// erişim için). Dashboard'a geçmek için sidebar'daki "Ana Sayfa" menü
// linki veya login sonrası otomatik yönlendirme kullanılır.
const HomeRoute = () => {
    const { loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    return <LandingPage />;
};

// App content with routing
const AppContent = () => {
    return (
        <BrowserRouter>
            <AppLayout>
            <React.Suspense fallback={<div className="flex items-center justify-center py-32"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
            <Routes>
                {/* Public Routes */}
                <Route
                    path="/login"
                    element={
                        <PublicRoute>
                            <LoginPage />
                        </PublicRoute>
                    }
                />
                <Route path="/signup" element={<TenantSignupPage />} />
                <Route path="/verify/:certificateNumber" element={<VerifyCertificatePage />} />
                <Route path="/verify" element={<VerifyCertificatePage />} />
                {/* Temsilci embed akışı — parent portal iframe içinde açar, login gerektirmez */}
                <Route path="/embed/training/:token" element={<EmbedTrainingPage />} />
                <Route path="/keycloak-callback" element={<KeycloakCallbackPage />} />
                <Route path="/kurumsal" element={<KurumsalPage />} />
                <Route path="/bireysel" element={<BireyselPage />} />
                <Route path="/tanitim" element={<LandingPage />} />
                <Route path="/demo" element={<DemoShowcasePage />} />
                <Route path="/denetim-kanit" element={<DenetimKanitPage />} />
                <Route path="/denetim-ozet" element={<DenetimOzetPage />} />
                <Route path="/sozlesme" element={<SozlesmePage />} />
                {/* Süreli imzalı denetçi paylaşım linki — hesap gerektirmez */}
                <Route path="/rapor-goruntule/:token" element={<RegulatorShareViewPage />} />
                <Route path="/kvkk-aydinlatma-metni" element={<KvkkAydinlatmaPage />} />
                <Route path="/cerez-politikasi" element={<CerezPolitikasiPage />} />
                <Route path="/kullanim-sartlari" element={<KullanimSartlariPage />} />

                {/* Super Admin Routes */}
                <Route
                    path="/super-admin"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminDashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/dashboard"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminDashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/packages"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminPackagesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/sectors"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminSectorsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/tenants"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminTenantsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/demo-requests"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminDemoRequestsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/tenants/new"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminTenantCreatePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/tenants/:tenantId"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminTenantDetailPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/cms"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminCMSPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/super-admin/regulatory-values"
                    element={
                        <ProtectedRoute requireSuperAdmin>
                            <SuperAdminRegulatoryValuesPage />
                        </ProtectedRoute>
                    }
                />

                {/* Tenant Admin Routes */}
                <Route
                    path="/tenant-admin"
                    element={
                        <ProtectedRoute requireTenantAdmin>
                            <TenantAdminDashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/curriculum"
                    element={
                        <ProtectedRoute requireTenantAdmin>
                            <TenantCurriculumPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/settings"
                    element={
                        <ProtectedRoute requireTenantAdmin>
                            <TenantSettingsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/instructors"
                    element={
                        <ProtectedRoute requireTenantAdmin>
                            <InstructorManagementPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/users"
                    element={
                        <ProtectedRoute requireTenantAdmin>
                            <UserManagementPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/webhooks"
                    element={
                        <ProtectedRoute requireTenantAdmin>
                            <WebhooksPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/bulletins"
                    element={
                        <ProtectedRoute requireAudit>
                            <BulletinManagePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/ic-dokumanlar"
                    element={
                        <ProtectedRoute requireAudit>
                            <InternalDocsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/department-assignments"
                    element={
                        <ProtectedRoute requireTenantAdmin>
                            <DepartmentAssignmentsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/tenant-admin/agent-training"
                    element={
                        <ProtectedRoute requireAudit>
                            <AgentTrainingPage />
                        </ProtectedRoute>
                    }
                />

                {/* Invite Accept Route (Public) */}
                <Route path="/invite/:inviteCode" element={<AcceptInvitePage />} />

                {/* Protected Routes - User */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <DashboardPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/egitimler"
                    element={
                        <ProtectedRoute>
                            <AllTrainingsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/mevzuat"
                    element={
                        <ProtectedRoute>
                            <MevzuatPortalPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/module/:moduleId"
                    element={
                        <ProtectedRoute>
                            <ModulePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/topic/:topicId"
                    element={
                        <ProtectedRoute>
                            <TopicPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/lesson/:lessonId"
                    element={
                        <ProtectedRoute>
                            <LessonPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/quiz/:sectionId"
                    element={
                        <ProtectedRoute>
                            <QuizPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/quiz-attempts/:attemptId"
                    element={
                        <ProtectedRoute>
                            <QuizAnswerSheetPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/certificate"
                    element={
                        <ProtectedRoute>
                            <CertificatePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute>
                            <ProfilePage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/weak-topics"
                    element={
                        <ProtectedRoute>
                            <WeakTopicsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/content-updates"
                    element={
                        <ProtectedRoute>
                            <ContentUpdatesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/leaderboard"
                    element={
                        <ProtectedRoute>
                            <LeaderboardPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/exams"
                    element={
                        <ProtectedRoute>
                            <ExamsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/live-sessions"
                    element={
                        <ProtectedRoute>
                            <LiveSessionsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/live/:sessionId"
                    element={
                        <ProtectedRoute>
                            <LiveRoomPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/instructor"
                    element={
                        <ProtectedRoute requireInstructor>
                            <InstructorDashboardPage />
                        </ProtectedRoute>
                    }
                />

                {/* Protected Routes - Auditor */}
                <Route
                    path="/auditor"
                    element={
                        <ProtectedRoute requireAuditor>
                            <AuditorPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/regulator-report"
                    element={
                        <ProtectedRoute requireAuditor>
                            <RegulatorReportPage />
                        </ProtectedRoute>
                    }
                />

                {/* Protected Routes - Admin */}
                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminDashboard />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/users"
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminUsers />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/content"
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminContent />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/reports"
                    element={
                        <ProtectedRoute adminOnly requireReports>
                            <AdminReports />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/audit-logs"
                    element={
                        <ProtectedRoute adminOnly requireAudit>
                            <AdminAuditLogs />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/settings"
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminSettings />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/bbb"
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminBBBPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/training-periods"
                    element={
                        <ProtectedRoute adminOnly>
                            <TrainingPeriodsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/notifications"
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminNotificationsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/documents"
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminDocumentsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/content-gen"
                    element={
                        <ProtectedRoute adminOnly>
                            <AdminContentGenPage />
                        </ProtectedRoute>
                    }
                />

                {/* Role-Based Dashboard Routes */}
                <Route
                    path="/manager"
                    element={
                        <ProtectedRoute requireReports>
                            <ManagerDashboardPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/manager/team/:userId"
                    element={
                        <ProtectedRoute requireReports>
                            <ManagerTeamMemberPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/compliance"
                    element={
                        <ProtectedRoute requireAudit>
                            <ComplianceDashboardPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/executive"
                    element={
                        <ProtectedRoute requireReports>
                            <ExecutiveDashboardPage />
                        </ProtectedRoute>
                    }
                />

                {/* Ana sayfa: oturumsuz → LandingPage, oturum açık → dashboard */}
                <Route path="/" element={<HomeRoute />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </React.Suspense>
            </AppLayout>
            <Toaster position="top-right" richColors />
        </BrowserRouter>
    );
};

function App() {
    return (
        <ThemeProvider defaultTheme="light">
            <AuthProvider>
                <ErrorBoundary>
                    <AppContent />
                </ErrorBoundary>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
